import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Chip from '@mui/material/Chip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { intersection, union } from 'lodash-es';
import { useMatch } from '../../contexts/MatchContext';
import * as api from '../../api';
import { getFlagSrc } from '../../utils/scoring';

export default function Squads() {
  const { t } = useTranslation();
  const { match } = useMatch();
  const [squads, setSquads] = useState(null);
  const [shooters, setShooters] = useState(null);

  useEffect(() => {
    if (!match) return;
    window.scrollTo(0, 0);

    Promise.all([
      api.getMatch(match.id),
      api.getShooters(match.id),
    ]).then(([matchRes, shootersRes]) => {
      setSquads(matchRes.data.squads || []);
      setShooters((shootersRes.data || []).filter((s) => !s.cancelledParticipation));
    });
  }, [match?.id]);

  if (!squads || !shooters) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (squads.length === 0) {
    return (
      <Typography variant="h6" sx={{ textAlign: 'center', mt: 2 }}>
        No squads have been published yet.
      </Typography>
    );
  }

  const toDivisionKey = (division) => {
    const map = {
      'standard': 'standard', 'production': 'production', 'open': 'open',
      'production-optics': 'productionOptics', 'optics': 'optics', 'classic': 'classic',
      'revolver': 'revolver', 'pcc-optics': 'pccOptics', 'pcc-iron': 'pccIron',
      'sg-open': 'sgOpen', 'sg-modified': 'sgModified', 'sg-standard': 'sgStandard',
      'sg-standard-manual': 'sgStandardManual',
    };
    return map[division] || division;
  };

  const toCategoryKey = (category) => {
    const map = {
      'regular': 'regular', 'senior': 'senior', 'super-senior': 'superSenior',
      'grand-senior': 'grandSenior', 'lady-senior': 'ladySenior', 'junior': 'junior',
      'super-junior': 'superJunior', 'grand-junior': 'grandJunior', 'lady-junior': 'ladyJunior',
      'lady-super-junior': 'ladySuperJunior', 'lady-grand-junior': 'ladyGrandJunior', 'lady': 'lady',
    };
    return map[category] || category;
  };

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', width: '90%' }}>
      <Grid container spacing={2}>
        {squads.map((squad) => {
          const allShootersForSquad = shooters.filter((s) => s.squad === squad.id).map((s) => s.id);
          const order = squad.order || [];
          const orderedIds = [...new Set([...order.filter((id) => allShootersForSquad.includes(id)), ...allShootersForSquad])];
          const orderedShooters = orderedIds.map((id) => shooters.find((s) => s.id === id)).filter(Boolean);

          return (
            <Grid key={squad.id} size={{ xs: 12 }}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Typography variant="h6">{squad.title}</Typography>
                    {squad.ro && (
                      <Chip label="RO" size="small" color="primary" sx={{ fontWeight: 800 }} />
                    )}
                    {squad.description && (
                      <Typography variant="body2" color="text.secondary">
                        {squad.description}
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ marginInlineStart: 'auto', marginInlineEnd: 2 }}>
                      {orderedShooters.length} {t('matches.shooters')}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {orderedShooters.map((shooter) => {
                    if (!shooter.publicId) return null;
                    const parts = (shooter.publicId || '').split('|');
                    const displayId = parts[parts.length - 1];
                    const countryCode = parts.length > 1 ? parts[0] : null;

                    return (
                      <Box key={shooter.id} sx={{ display: 'flex', alignItems: 'center', py: 0.75, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {countryCode && (
                              <img
                                alt={countryCode}
                                src={getFlagSrc({ countryCode, size: 16 })}
                                style={{ display: 'block' }}
                              />
                            )}
                            <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>
                              {displayId} / {shooter.name}
                            </Typography>
                          </Box>
                          <Typography variant="caption" sx={{ opacity: 0.6 }}>
                            {shooter.club}
                            {' | '}
                            {t(`divisions.${toDivisionKey(shooter.division)}`, { defaultValue: shooter.division })}
                            {' | '}
                            {t(`categories.${toCategoryKey(shooter.category)}`, { defaultValue: shooter.category })}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                  {orderedShooters.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No shooters assigned to this squad.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
